import React, { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import API from "../utils/api";

// ============================================================
// The daily field briefing
// ============================================================
// This screen deliberately does not re-draw the report in React. The briefing
// is one document — the same bytes are mailed to management at 7:45 and stored
// in daily_field_briefings — and rendering it a second time here would mean two
// versions that could quietly disagree about the same day. So the page shows
// the stored HTML itself, in an iframe, exactly as the inbox shows it.
//
// The iframe is sandboxed to allow-same-origin only: no scripts run, and the
// briefing's own light palette is kept intact rather than inheriting whatever
// the surrounding dashboard is doing.

const todayIST = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });

const stamp = (v) => v
  ? new Date(String(v).replace(" ", "T")).toLocaleString("en-GB", {
      timeZone: "Asia/Kolkata", day: "numeric", month: "short",
      hour: "numeric", minute: "2-digit", hour12: true,
    })
  : "";

export default function FieldBriefing({ accent = "#E31E24" }) {
  const [date, setDate]       = useState(todayIST());
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]       = useState(false);
  const [height, setHeight]   = useState(900);
  const frame = useRef(null);

  const load = useCallback(async (fresh = false) => {
    fresh ? setBusy(true) : setLoading(true);
    try {
      const res = await API.get(`/reports/field-briefing?date=${date}${fresh ? "&fresh=1" : ""}`);
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not load the briefing");
      setData(null);
    } finally { setLoading(false); setBusy(false); }
  }, [date]);

  useEffect(() => { load(false); }, [load]);

  // The document has no scripts of its own, so nothing inside can report its
  // own height — measure it on load instead, and again shortly after, once the
  // tables have finished laying out.
  const fit = () => {
    const doc = frame.current?.contentDocument;
    if (!doc?.body) return;
    const measure = () => setHeight(Math.max(600, doc.body.scrollHeight + 32));
    measure();
    setTimeout(measure, 250);
  };

  const bar = {
    display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center",
    background: "#fff", border: "1px solid #e2e8f0", borderRadius: 13,
    padding: "12px 14px", marginBottom: 12,
  };
  const btn = (primary) => ({
    padding: "8px 14px", borderRadius: 9, fontSize: 13, fontWeight: 600,
    cursor: busy ? "wait" : "pointer",
    border: primary ? "none" : "1px solid #cbd5e1",
    background: primary ? accent : "#fff",
    color: primary ? "#fff" : "#334155",
  });
  const pill = (bg, border, text) => ({
    fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 999,
    background: bg, border: `1px solid ${border}`, color: text, letterSpacing: 0.2,
  });

  return (
    <div>
      <div style={bar}>
        <input
          type="date"
          value={date}
          max={todayIST()}
          onChange={(e) => setDate(e.target.value || todayIST())}
          style={{ padding: "8px 10px", borderRadius: 9, border: "1px solid #cbd5e1", fontSize: 14 }}
        />
        <button style={btn(false)} onClick={() => load(true)} disabled={busy}>
          {busy ? "Rebuilding…" : "Rebuild from today's data"}
        </button>
        <button
          style={btn(true)}
          onClick={() => frame.current?.contentWindow?.print()}
          disabled={!data?.html}
        >
          Print
        </button>

        <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
          {data?.stored
            ? <span style={pill("#ecfdf5", "#a7f3d0", "#047857")}>Stored copy</span>
            : <span style={pill("#f8fafc", "#e2e8f0", "#475569")}>Rebuilt just now</span>}
          {data?.sent_email
            ? <span style={pill("#eff6ff", "#bfdbfe", "#1d4ed8")}>Emailed</span>
            : <span style={pill("#fffbeb", "#fde68a", "#b45309")}>Not emailed yet</span>}
          {data?.generated_at &&
            <span style={{ fontSize: 12, color: "#64748b" }}>Built {stamp(data.generated_at)}</span>}
        </div>
      </div>

      {/* The stored copy wins on a re-read: what management were sent at 7:45 is
          what should come back, not a fresh computation that has picked up every
          visit edited since. "Rebuild" is a look at the day as it stands now and
          never overwrites what went out. */}
      <p style={{ fontSize: 12, color: "#64748b", margin: "0 0 12px 2px" }}>
        {data?.stored
          ? "This is the copy that was generated and sent for this date — not a fresh calculation."
          : "This is a live rebuild. It has not replaced the copy on record for this date."}
      </p>

      {loading ? (
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 13, padding: 40, textAlign: "center", color: "#64748b" }}>
          Loading the briefing…
        </div>
      ) : !data?.html ? (
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 13, padding: 40, textAlign: "center", color: "#64748b" }}>
          Nothing for this date yet. The briefing is built at 7:45 pm — use “Rebuild” to see the day as it stands.
        </div>
      ) : (
        <iframe
          ref={frame}
          title={`Field briefing ${data.date}`}
          srcDoc={data.html}
          sandbox="allow-same-origin allow-modals"
          onLoad={fit}
          style={{
            width: "100%", height, border: "1px solid #e2e8f0",
            borderRadius: 13, background: "#fff", display: "block",
          }}
        />
      )}
    </div>
  );
}
